/**
 * IMAGE TYPES: the seven kinds of picture a pathology case holds, as the upload
 * form asks for them ("Type") and the viewer's navigator sections them. Mirrors
 * server/src/catalog/image_types.ts, the one definition; keep them the same.
 */

export const IMAGE_TYPES = ['wsi', 'photomicrograph', 'gross', 'electron_microscopy', 'immunofluorescence', 'radiography', 'document'] as const;
export type ImageType = (typeof IMAGE_TYPES)[number];
/** How an image is stored and opened: a slide's pyramid, a flat picture, or a document (a PDF). */
export type ImageKind = 'wsi' | 'flat' | 'document';

export const isImageType = (v: unknown): v is ImageType => typeof v === 'string' && (IMAGE_TYPES as readonly string[]).includes(v);

export const IMAGE_TYPE_LABELS: Record<ImageType, string> = {
  wsi: 'Whole-Slide Imaging',
  photomicrograph: 'Photomicrograph',
  gross: 'Gross',
  electron_microscopy: 'Electron Microscopy',
  immunofluorescence: 'Immunofluorescence',
  radiography: 'Radiography',
  document: 'Documents',
};

/** Each type's short name, under its icon in the viewer's Type bar. */
export const TYPE_ACRONYMS: Record<ImageType, string> = {
  wsi: 'WSI',
  photomicrograph: 'PM',
  gross: 'GR',
  electron_microscopy: 'EM',
  immunofluorescence: 'IF',
  radiography: 'XR',
  document: 'DOC',
};

/**
 * Which types a file may be by what it certainly is: a scanner's slide is a
 * slide (or an immunofluorescence slide), and a PDF is a document. Nothing is
 * certainly flat, so the 'flat' column only picks immunofluorescence's
 * modality (modalityOf): how a picture is stored — a pyramid, or not — never
 * limits its type. A photographed form is a flat picture typed Documents.
 */
export const IMAGE_TYPE_KINDS: Record<ImageType, readonly ImageKind[]> = {
  wsi: ['wsi'],
  photomicrograph: ['flat'],
  gross: ['flat'],
  electron_microscopy: ['flat'],
  immunofluorescence: ['wsi', 'flat'],
  radiography: ['flat'],
  document: ['flat', 'document'],
};

/**
 * The short tag on an image's tile (Home's recent uploads, search): what it
 * is, not how it is stored. An X-ray is kept as a pyramid like a slide, and is
 * no slide.
 */
export const imageTag = (kind: ImageKind, type: ImageType | null | undefined): string =>
  type === 'radiography' ? 'X-ray' : kind === 'wsi' ? 'WSI' : kind === 'document' ? 'Document' : 'Image';

export const imageTypesFor = (kind: ImageKind): ImageType[] => IMAGE_TYPES.filter((t) => IMAGE_TYPE_KINDS[t].includes(kind));

/** Whether a type can describe a file of this kind; an unknown kind (null) rules nothing out. */
export const typeFits = (type: ImageType, kind: ImageKind | null): boolean => kind === null || IMAGE_TYPE_KINDS[type].includes(kind);

/**
 * The X-ray SOP classes, Digital X-Ray For Presentation and For Processing
 * (server/src/catalog/image_types.ts RADIOGRAPH_SOP_CLASSES, the same list): a
 * file of one is certainly Radiography, whatever the converter stores it as
 * (a tiled pyramid, convert sources/radiograph.py).
 */
export const RADIOGRAPH_SOP_CLASSES: readonly string[] = ['1.2.840.10008.5.1.4.1.1.1.1', '1.2.840.10008.5.1.4.1.1.1.1.1'];

/**
 * Whether a Type may be chosen for a file: a file certain of its Type (an
 * X-ray, by its header) takes only that one; any other, every Type its kind
 * fits (typeFits).
 */
export const typeAllowed = (type: ImageType, kind: ImageKind | null, certainType: ImageType | null = null): boolean =>
  certainType ? type === certainType : typeFits(type, kind);

/**
 * What a file certainly is from its name alone: a scanner's own format is a
 * slide, and so is an OME-TIFF (the converter routes every one through the
 * scanner path); a PDF is a document. Nothing else says: a JPEG, PNG or TIFF
 * is a flat picture when small and a slide when the converter pyramidises it,
 * a DICOM file can be any of them, a packed slide names no extension —
 * unknown until converted (the converter settles the kind from the SOP class
 * it writes).
 */
export function certainKind(name: string): ImageKind | null {
  const lower = name.toLowerCase();
  const ext = lower.split('.').pop() ?? '';
  if (ext === 'pdf') return 'document';
  return ext === 'svs' || ext === 'ndpi' || ext === 'mrxs' || ext === 'scn' || lower.endsWith('.ome.tif') || lower.endsWith('.ome.tiff') ? 'wsi' : null;
}

/** What a file is until somebody says otherwise: a slide is a slide, a document a document, a flat picture a gross photo. */
export const defaultImageType = (kind: ImageKind): ImageType => (kind === 'flat' ? 'gross' : kind === 'wsi' ? 'wsi' : 'document');

/** The DICOM Modality (0008,0060) an image of this type is written with. */
export function modalityOf(type: ImageType, kind: ImageKind): string {
  switch (type) {
    case 'wsi':
      return 'SM';
    case 'immunofluorescence':
      return kind === 'wsi' ? 'SM' : 'GM';
    case 'photomicrograph':
    case 'electron_microscopy':
      return 'GM';
    case 'gross':
      return 'XC';
    case 'radiography':
      return 'DX';
    case 'document':
      return 'DOC';
  }
}

/**
 * The type a modality says, for a picture nobody chose a type for — the
 * server's imageTypeOf and the database's image_type_of() (0099). The kind
 * matters because a microscopy modality is a slide only as a pyramid, and a
 * document is a document whatever its file says. Anything else is no type at
 * all ("Other").
 */
export function imageTypeOf(modality: string | null, illumination: string | null, kind: ImageKind): ImageType | null {
  if (kind === 'document') return 'document';
  const microscopy = modality === 'SM' || modality === 'GM';
  if ((illumination === '111743' || illumination === '111749') && (microscopy || kind === 'wsi')) return 'immunofluorescence';
  if (kind === 'wsi' && (microscopy || modality === 'XC')) return 'wsi';
  if (microscopy) return 'photomicrograph';
  if (modality === 'XC') return 'gross';
  if (modality === 'DX' || modality === 'CR' || modality === 'RG') return 'radiography';
  if (modality === 'DOC') return 'document';
  return null;
}

export const LATERALITIES = ['L', 'R', 'B'] as const;
export type Laterality = (typeof LATERALITIES)[number];

/** CID 244 (Laterality), SNOMED CT, as stored in the catalogue and written to (0008,2230). */
export const LATERALITY_CODES: Record<Laterality, { code: string; scheme: 'SCT'; meaning: string }> = {
  L: { code: '7771000', scheme: 'SCT', meaning: 'Left' },
  R: { code: '24028007', scheme: 'SCT', meaning: 'Right' },
  B: { code: '51440002', scheme: 'SCT', meaning: 'Bilateral' },
};

export const isLaterality = (v: unknown): v is Laterality => typeof v === 'string' && (LATERALITIES as readonly string[]).includes(v);

/** The laterality a stored code stands for, or null for none (or a code that is not one of the three). */
export const lateralityOfCode = (code: string | null | undefined): Laterality | null =>
  LATERALITIES.find((l) => LATERALITY_CODES[l].code === code) ?? null;
